import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { organizationsAuditLogsFiltersRetrieveOptions } from 'src/api/main'
// Direct import (not the `src/components` barrel) to avoid a cycle: the barrel
// pulls in components that import `src/hooks`, and this hook lives in `src/hooks`.
import {
  buildFacetOptions,
  type FacetOption
} from 'src/components/FilterSelect'

import { useDebouncedSearch } from './useDebouncedSearch'

// How long a fetched option list is trusted before a page entry re-fetches it.
// The values churn while a workspace is young and then go quiet for hours, so
// this trades a bounded staleness window for not running the backend's DISTINCT
// scan on every visit.
export const OPTIONS_STALE_TIME_MS = 5 * 60 * 1000

// Fields the `/filters` facet endpoint can list distinct values for.
export type AuditFacetField =
  | 'action'
  | 'actor_type'
  | 'category'
  | 'channel'
  | 'entity_type'
  | 'event_type'
  | 'service'
  | 'severity'

/**
 * Options for one audit-log facet dropdown, backed by the `/filters` endpoint.
 * Returns the capped/searchable options plus the `onSearch` handler wired for a
 * `FilterSelect`. Search is a *debounced client-side* filter over the fetched
 * distinct values — typing never re-renders the caller (see `useDebouncedSearch`).
 *
 * - `enabled: false` defers the request (e.g. a filter hidden inside a "more
 *   filters" popover until it is first opened).
 * - `applicationId` scopes the distinct values to one application (app-detail).
 */
export const useFacetFilter = (params: {
  organizationId: number
  field: AuditFacetField
  selected: string
  getLabel: (value: string) => string
  enabled?: boolean
  applicationId?: number
}): { options: FacetOption[]; onSearch: (text: string) => void } => {
  const { organizationId, field, selected, getLabel, enabled, applicationId } =
    params
  const [search, onSearch] = useDebouncedSearch(300)

  const { data } = useQuery({
    ...organizationsAuditLogsFiltersRetrieveOptions({
      path: { organization_id: organizationId },
      query: {
        field,
        ...(applicationId ? { application_id: applicationId } : {})
      }
    }),
    enabled: enabled ?? organizationId > 0,
    // Opt out of the app-wide refetchOnMount:false, but only re-fetch once the
    // list is stale — a value that first appears now becomes filterable within
    // OPTIONS_STALE_TIME_MS instead of needing a page reload.
    refetchOnMount: true,
    staleTime: OPTIONS_STALE_TIME_MS
  })

  const options = useMemo(
    () => buildFacetOptions(data?.values, selected, search, getLabel),
    [data?.values, selected, search, getLabel]
  )

  return { options, onSearch }
}
